// Recipe Storage Form
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { ChefHat, Plus } from 'lucide-react';

interface RecipeFormProps {
  onAdd: (item: { title: string; notes?: string; metadata?: any }) => void;
}

export default function RecipeForm({ onAdd }: RecipeFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [title, setTitle] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [instructions, setInstructions] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [servings, setServings] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listName.trim()) {
      alert('Please give your recipe collection a name!');
      return;
    }
    if (!title.trim()) return;

    const recipeNotes = `
**Ingredients:**
${ingredients}

**Instructions:**
${instructions}

${cookTime ? `**Cook Time:** ${cookTime}` : ''}
${servings ? `**Servings:** ${servings}` : ''}
    `.trim();

    onAdd({
      title: `🍳 ${title.trim()}`,
      notes: recipeNotes,
      metadata: {
        type: 'recipe',
        listName: listName.trim(),
        recipeName: title.trim(),
        ingredients,
        instructions,
        cookTime: cookTime || null,
        servings: servings || null
      }
    });

    // Reset form
    setListName('');
    setTitle('');
    setIngredients('');
    setInstructions('');
    setCookTime('');
    setServings('');
    setIsOpen(false);
  };

  return (
    <>
      {/* Add Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <ChefHat className="w-5 h-5" />
        Add Recipe
      </button>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Recipe"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipe Collection Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Recipe Collection Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Family Favorites, Quick Meals, Holiday Recipes"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this recipe collection to organize different recipe groups</p>
          </div>
          
          {/* Recipe Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Recipe Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base"
              placeholder="e.g., Mom's Lasagna, Chocolate Chip Cookies"
              required
              autoFocus
            />
          </div>

          {/* Cook Time & Servings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Cook Time
              </label>
              <input
                type="text"
                value={cookTime}
                onChange={(e) => setCookTime(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base"
                placeholder="e.g., 45 minutes, 1 hour"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Servings
              </label>
              <input
                type="text"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base"
                placeholder="e.g., 4, 6-8 people"
              />
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Ingredients
            </label>
            <textarea
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base resize-none"
              placeholder="List ingredients (one per line):
- 2 cups flour
- 1 cup sugar
- 3 eggs"
            />
          </div>

          {/* Instructions */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Instructions
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={8}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base resize-none"
              placeholder="Step-by-step instructions:
1. Preheat oven to 350°F
2. Mix dry ingredients
3. Add wet ingredients..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6"
          >
            <Plus className="w-5 h-5" />
            Add Recipe
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
